import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { StoryCard, storyCardApi, StoryCardByIdArgs } from '../services';
import {
  addCardToBookmarks,
  addComment,
  deleteCard,
  deleteComment,
  editCard,
  editComment,
  eventDelete,
  eventUpdateChannels,
  hideComment,
  highlightComment,
  loadMoreComments,
  pollVote,
  reactOnCard,
  reactOnComment,
  removeCardFromBookmarks,
  repostCard,
  toggleCardSection,
  updateCardEvent,
} from './common';
import {
  addCommentReducer,
  deleteCommentReducer,
  editCommentReducer,
  hideCommentReducer,
  highlightCommentReducer,
  pollVoteReducer,
  reactOnCardReducer,
  reactOnCommentReducer,
} from './extraReducers';

interface CardDetailsState {
  isFetched: boolean;
  card?: StoryCard;
}

const initialState: CardDetailsState = {
  isFetched: false,
  card: undefined,
};

export const getCard = createAsyncThunk(
  'getCard',
  async (args: StoryCardByIdArgs, { dispatch }) => {
    return await dispatch(storyCardApi.endpoints.storyCardById.initiate(args)).unwrap();
  }
);

const cardDetailsSlice = createSlice({
  name: 'cardDetails',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getCard.pending, (state) => {
        state.card = undefined;
      })
      .addCase(getCard.fulfilled, (state, action) => {
        state.isFetched = true;
        state.card = action.payload;
      })
      .addCase(addComment.fulfilled, (state, action) => {
        const { itemId, comment } = action.payload;

        const { card } = state;

        if (!card || itemId !== card.id) {
          return;
        }

        addCommentReducer({ card, comment });
      })
      .addCase(editComment.fulfilled, (state, action) => {
        const { comment } = action.payload;
        const { storyCardId } = comment;
        const { card } = state;

        if (!card || storyCardId !== card.id) {
          return;
        }

        editCommentReducer({ card, comment });
      })
      .addCase(hideComment.fulfilled, (state, action) => {
        const { comment } = action.payload;
        const { storyCardId } = comment;
        const { card } = state;

        if (!card || storyCardId !== card.id) {
          return;
        }

        hideCommentReducer({ card, comment });
      })
      .addCase(highlightComment.fulfilled, (state, action) => {
        const { comment } = action.payload;
        const { storyCardId } = comment;
        const { card } = state;

        if (!card || storyCardId !== card.id) {
          return;
        }

        highlightCommentReducer({ card, comment });
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        const { deleted, cardId, commentId, parentId, repliesCount } = action.payload;

        if (!deleted) {
          return;
        }

        const { card } = state;

        if (!card || cardId !== card.id) {
          return;
        }

        deleteCommentReducer({ card, commentId, parentId, repliesCount });
      })
      .addCase(reactOnComment.fulfilled, (state, action) => {
        const { reactions, cardId, commentId } = action.payload;
        const { card } = state;

        if (!card || cardId !== card.id) {
          return;
        }

        reactOnCommentReducer({ card, commentId, reactions });
      })
      .addCase(reactOnCard.fulfilled, (state, action) => {
        const { cardId, reaction, reactions } = action.payload;
        const { card } = state;

        if (!card || cardId !== card.id) {
          return;
        }

        reactOnCardReducer({ card, reaction, reactions });
      })
      .addCase(loadMoreComments.fulfilled, (state, action) => {
        const { itemId, items, parentId } = action.payload;
        const { card } = state;

        if (!card || itemId !== card.id) {
          return;
        }

        const { comments } = card;

        if (parentId) {
          const replies = comments.find(({ id }) => id === parentId)?.replies;

          items.forEach((reply) => {
            if (!replies?.find(({ id }) => id === reply.id)) {
              replies?.push(reply);
            }
          });
        } else {
          comments.push(...items.filter(({ id }) => !comments.map(({ id }) => id)?.includes(id)));
        }
      })
      .addCase(editCard, (state, action) => {
        const { id: storyCardId } = action.payload;

        if (!state.card || state.card.id !== storyCardId) {
          return;
        }

        state.card = action.payload;
      })
      .addCase(deleteCard.fulfilled, (state, action) => {
        const { deleted, storyCardId } = action.payload;
        const { card } = state;

        if (!deleted || !card || storyCardId !== card.id) {
          return;
        }

        state.card = undefined;
      })
      .addCase(repostCard.fulfilled, (state, action) => {
        const storyCard = action.payload;

        if (!state.card || storyCard.id !== state.card.id) {
          return;
        }

        state.card.dateToDisplay = storyCard.dateToDisplay;
      })
      .addCase(toggleCardSection.fulfilled, (state, action) => {
        const { payload } = action.payload;

        if (!state.card || payload.id !== state.card.id) {
          return;
        }

        state.card.section = payload.section;
      })
      .addCase(addCardToBookmarks.fulfilled, (state, action) => {
        const { payload } = action.payload;

        if (!state.card || !payload) {
          return;
        }

        state.card.isBookmarked = true;
      })
      .addCase(removeCardFromBookmarks.fulfilled, (state, action) => {
        const { payload } = action.payload;

        if (!state.card || !payload) {
          return;
        }

        state.card.isBookmarked = false;
      })
      .addCase(pollVote.fulfilled, (state, action) => {
        const { payload, selectedVoteIds } = action.payload;
        const { card } = state;

        if (!card || !payload) {
          return;
        }

        pollVoteReducer({ card, selectedVoteIds });
      })
      .addCase(eventDelete.fulfilled, (state, action) => {
        const { deleted, eventId } = action.payload;

        if (!deleted || !state.card || state.card.eventId !== eventId) {
          return;
        }

        state.card.event = null;
      })
      .addMatcher(
        isAnyOf(updateCardEvent.fulfilled, eventUpdateChannels.fulfilled),
        (state, action) => {
          if (!state.card || state.card.eventId !== action.payload.id) {
            return;
          }

          state.card.event = action.payload;
        }
      );
  },
});

export default cardDetailsSlice.reducer;
